import React, { useEffect, useState } from 'react';
import type { QuestionnaireStatus } from './marketProfileStore';
import { formatReviewCountdown, reviewTimeRemaining } from './marketProfileStore';

export function StatusPill({ status }: { status: QuestionnaireStatus }) {
  if (status === 'complete') {
    return <span className="dcf-status-pill dcf-status-pill--active">Заполнено</span>;
  }
  if (status === 'pending_review') {
    return (
      <span className="dcf-status-pill dcf-mp-pill--review">
        <i className="fas fa-hourglass-half" aria-hidden="true" />
        На проверке
      </span>
    );
  }
  if (status === 'draft') {
    return <span className="dcf-status-pill dcf-mp-pill--draft">Черновик</span>;
  }
  return <span className="dcf-status-pill dcf-status-pill--hidden">Не начато</span>;
}

export function ReviewCountdown({
  reviewSubmittedAt,
  showLabel = true,
}: {
  reviewSubmittedAt: string;
  onComplete?: () => void;
  showLabel?: boolean;
}) {
  const [remaining, setRemaining] = useState(() => reviewTimeRemaining(reviewSubmittedAt) ?? 0);

  useEffect(() => {
    const tick = () => {
      const next = reviewTimeRemaining(reviewSubmittedAt) ?? 0;
      setRemaining(next);
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [reviewSubmittedAt]);

  if (remaining <= 0) {
    return (
      <div className="dcf-mp-review-countdown dcf-mp-review-countdown--done">
        <i className="fas fa-hourglass-half" aria-hidden="true" />
        <span>Ожидает подтверждения администратора</span>
      </div>
    );
  }

  return (
    <div className="dcf-mp-review-countdown">
      {showLabel && (
        <span className="dcf-mp-review-countdown__label">Ожидаемый срок проверки</span>
      )}
      <span className="dcf-mp-review-countdown__value">{formatReviewCountdown(remaining)}</span>
    </div>
  );
}

export function ProgressBar({ value }: { value: number }) {
  const clamped = Math.max(0, Math.min(100, value));
  return (
    <div className="dcf-mp-progress" role="progressbar" aria-valuenow={clamped} aria-valuemin={0} aria-valuemax={100}>
      <div className="dcf-mp-progress__fill" style={{ width: `${clamped}%` }} />
    </div>
  );
}

/** Trust-плашка рядом с чувствительными полями */
export function SensitiveDataNotice({ compact }: { compact?: boolean }) {
  if (compact) {
    return (
      <p className="dcf-mp-trust-inline">
        <i className="fas fa-shield-halved" aria-hidden="true" />
        Данные используются для агрегированных расчетов. Индивидуальные значения не раскрываются другим участникам.
      </p>
    );
  }
  return (
    <div className="dcf-aside-card dcf-mp-trust-card">
      <div className="dcf-aside-card__title">
        <i className="fas fa-shield-halved" aria-hidden="true" />
        {' '}Как используются данные
      </div>
      <p className="dcf-aside-card__text">
        Введенные данные используются для формирования агрегированных рыночных ориентиров.
        Индивидуальные значения не отображаются другим участникам как отдельная запись.
      </p>
      <ul className="dcf-mp-trust-list">
        <li>Публичный профиль может отображаться через псевдоним.</li>
        <li>Компания, email и телефон не показываются в сообществе.</li>
        <li>Компенсационные данные используются только в агрегированном виде.</li>
        <li>Вы можете обновить данные позже.</li>
      </ul>
    </div>
  );
}

export function MpStepper({
  steps,
  current,
}: {
  steps: string[];
  current: number;
}) {
  return (
    <ol className="dcf-mp-stepper" aria-label="Шаги анкеты">
      {steps.map((label, i) => {
        const stateClass =
          i < current ? 'dcf-mp-stepper__item--done'
            : i === current ? 'dcf-mp-stepper__item--current'
              : '';
        return (
          <li key={label} className={`dcf-mp-stepper__item ${stateClass}`.trim()}>
            <span className="dcf-mp-stepper__dot">
              {i < current ? <i className="fas fa-check" aria-hidden="true" /> : i + 1}
            </span>
            <span className="dcf-mp-stepper__label">{label}</span>
          </li>
        );
      })}
    </ol>
  );
}

export function MpField({
  label,
  required,
  hint,
  children,
  htmlFor,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  children: React.ReactNode;
  htmlFor?: string;
}) {
  const Tag = htmlFor ? 'label' : 'span';
  return (
    <div className="dcf-field">
      <Tag className="dcf-field__label" {...(htmlFor ? { htmlFor } : {})}>
        {label}
        {required && <span className="dcf-mp-required">*</span>}
      </Tag>
      {children}
      {hint && <p className="dcf-field__hint">{hint}</p>}
    </div>
  );
}

export function ReviewRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="dcf-mp-review-row">
      <dt>{label}</dt>
      <dd>{value || '—'}</dd>
    </div>
  );
}
